import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import { CampaignsRepository } from '../../domain/repositories/CampaignsRepository'
import { CampaignsService } from '@/backend/market/services/CampaignsService'
import type { CampaignsFilterEntity } from '../../domain/entities/CampaignsFilterEntity'
import { CampaignsFilterModel } from '../models/CampaignsFilterModel'
import { CampaignsMiniModel } from '../models/CampaignsMiniMode'
import type { CampaignsEntity } from '../../domain/entities/CampaignsEntity'
import { CampaignsModel } from '../models/CampaignsModel'
import type { CreateCampaignEntity } from '../../domain/entities/CreateCampaignEntity'
import { CreateCampaignModel } from '../models/CreateCampaignModel'
import type {
  CampaignAdminDto,
  CampaignRegionsRequestAdmin,
  CampaignUserAdminDto,
  PostCampaignAdminRequest
} from '@/backend/market'
import type { BranchCampaignsFilterEntity } from '../../domain/entities/BranchCampaignsFilterEntity'
import { BranchCampaignsFilterModel } from '../models/BranchCampaignsFilterModel'
import type { BranchCampaignsMiniEntity } from '../../domain/entities/BranchCampaignsMiniEntity'
import { BranchCampaignsMiniModel } from '../models/BranchCampaignsMiniModel'
import type { UserMiniEntity } from '@/features/user/domain/entities/UserMiniEntity'
import type { ChannelMiniEntity } from '@/features/channel/domain/entities/ChannelMiniEntity'
import type { ServiceMiniEntity } from '@/features/service/domain/entities/ServiceMiniEntity'
import { UserMiniModel } from '@/features/user/data/models/UserMiniModel'
import type { UserGroupMiniEntity } from '@/features/userGroup/domain/entities/UserGroupMiniEntity'
import type { ChannelCampaignEntity } from '../../domain/entities/ChannelCampaignEntity'
import type { ServiceCampaignEntity } from '../../domain/entities/ServiceCampaignEntity'
import type { CampaignRegionsEntity } from '../../domain/entities/CampaignRegionsEntity'
import { CampaignRegionsModel } from '../models/CampaignRegionsModel'

export class CampaignsRepositoryImpl implements CampaignsRepository {
  async getCampaigns(
    filterData: CampaignsFilterEntity
  ): Promise<PaginateEntity<CampaignsMiniModel[]> | { error: true }> {
    const campaignFilterModel = new CampaignsFilterModel(filterData)
    try {
      const response = await CampaignsService.getCampaignsAdmin(campaignFilterModel)
      const campaignMiniModels = response.items?.map(
        (item: CampaignAdminDto) => new CampaignsMiniModel(item)
      )
      const responseModel = new PaginateEntity<CampaignsMiniModel[]>(response, campaignMiniModels)

      return responseModel
    } catch {
      return { error: true }
    }
  }
  async getCampaignById(id: number): Promise<CampaignsEntity | { error: true }> {
    try {
      const response = await CampaignsService.getCampaignAdmin({ id })

      const campaignModel = new CampaignsModel(response)
      return campaignModel
    } catch {
      return { error: true }
    }
  }

  async getCampaignsByBranch(
    filterData: BranchCampaignsFilterEntity
  ): Promise<BranchCampaignsMiniEntity[] | { error: true }> {
    const filterDataModel = new BranchCampaignsFilterModel(filterData)
    try {
      const response = await CampaignsService.getCampaignIncludeBranchesAdmin(filterDataModel)
      const campaignModels = response.branches?.map(
        (item: BranchCampaignsMiniEntity) => new BranchCampaignsMiniModel(item)
      )
      return campaignModels ?? []
    } catch {
      return { error: true }
    }
  }
  async getCampaignUsers(id: number): Promise<UserMiniEntity[] | { error: true }> {
    try {
      const response = await CampaignsService.getCampaignUsersAdmin({
        id
      })
      const campaignModels = response.users?.map(
        (item: CampaignUserAdminDto) => new UserMiniModel(item)
      )
      return campaignModels ?? []
    } catch {
      return { error: true }
    }
  }
  async getCampaignChannels(id: number): Promise<ChannelCampaignEntity[] | { error: true }> {
    try {
      const response = await CampaignsService.getCampaignChannelsAdmin({
        id
      })

      return response.channels ?? []
    } catch {
      return { error: true }
    }
  }
  async getCampaignServices(id: number): Promise<ServiceCampaignEntity[] | { error: true }> {
    try {
      const response = await CampaignsService.getCampaignServicesAdmin({
        id
      })

      return response.services ?? []
    } catch {
      return { error: true }
    }
  }

  async getCampaignGroups(id: number): Promise<UserGroupMiniEntity[] | { error: true }> {
    try {
      const response = await CampaignsService.getCampaignGroupsAdmin({
        id
      })

      return response.groups ?? []
    } catch {
      return { error: true }
    }
  }

  async getCampaignsExcludedByBranch(
    filterData: BranchCampaignsFilterEntity
  ): Promise<BranchCampaignsMiniEntity[] | { error: true }> {
    const filterDataModel = new BranchCampaignsFilterModel(filterData)
    try {
      const response = await CampaignsService.getCampaignExcludedBranchesAdmin(filterDataModel)
      const campaignModels = response.branches?.map(
        (item: BranchCampaignsMiniEntity) => new BranchCampaignsMiniModel(item)
      )
      return campaignModels ?? []
    } catch {
      return { error: true }
    }
  }

  async createCampaign(data: CreateCampaignEntity): Promise<{ success: true } | { error: true }> {
    const createCampaignModel = new CreateCampaignModel(data)
    try {
      await CampaignsService.postCampaignAdmin({
        requestBody: createCampaignModel
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateCampaign(
    id: number,
    data: CreateCampaignEntity
  ): Promise<{ success: true } | { error: true }> {
    const createCampaignModel = new CreateCampaignModel(data)
    try {
      await CampaignsService.putCampaignAdmin({
        id,
        requestBody: createCampaignModel as PostCampaignAdminRequest
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateCampaignGroups(
    id: number,
    groups: UserGroupMiniEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CampaignsService.updateCampaignGroupsAdmin({
        id,
        requestBody: {
          groupIds: groups.map((group) => group.id!)
        }
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getCampaignRegions(id: number): Promise<CampaignRegionsEntity[] | { error: true }> {
    try {
      const response = await CampaignsService.getCampaignRegionsAdmin({ id })

      return response.regions ?? []
    } catch {
      return { error: true }
    }
  }
  async updateCampaignRegions(
    id: number,
    regions: CampaignRegionsEntity[]
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateCampaignRegionsModels = regions.map(
      (region) => new CampaignRegionsModel(region)
    )
    try {
      await CampaignsService.updateRegionsAdmin({
        id,
        requestBody: {
          regions: createUpdateCampaignRegionsModels as CampaignRegionsRequestAdmin[]
        }
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async deleteCampaign(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await CampaignsService.deleteCampaignAdmin({ id })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateCampaignIncludeBranch(
    id: number,
    branches: BranchCampaignsMiniEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CampaignsService.updateCampaignIncludeBranchesAdmin({
        id,
        requestBody: {
          branchIds: branches.map((branch) => branch.id!)
        }
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateCampaignExcludeBranch(
    id: number,
    branches: BranchCampaignsMiniEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CampaignsService.updateCampaignExcludedBranchesAdmin({
        id,
        requestBody: {
          branchIds: branches.map((branch) => branch.id!)
        }
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateCampaignUsers(
    id: number,
    users: UserMiniEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CampaignsService.updateCampaignUsersAdmin({
        id,
        requestBody: {
          userIds: users.map((user) => user.id!)
        }
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateCampaignChannels(
    id: number,
    channels: ChannelMiniEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CampaignsService.updateCampaignChannelsAdmin({
        id,
        requestBody: {
          channelIds: channels.map((channel) => channel.id!)
        }
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateCampaignServices(
    id: number,
    services: ServiceMiniEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CampaignsService.updateCampaignServicesAdmin({
        id,
        requestBody: {
          serviceIds: services.map((service) => service.id!)
        }
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
